/**
 * Data API Server Client
 * A server-side client that behaves like Clerk's server client
 */

import { createDataApiClient } from './client'
import type {
  SearchRequest,
  SearchResponse,
  SpecimenByFilterRequest,
  SpecimenByIdRequest,
  SpecimenCountRequest,
  SpecimenCountResponse,
  SpecimenResponse,
  SpecimenFilterOptionsResponse,
  AutocompleteRequest,
  AutocompleteResponse,
  OrganizationRequest,
  OrganizationResponse,
} from './'

/**
 * Data API Server Client
 * Similar to Clerk's server client, but for the Data API
 */
export class DataApiServerClient {
  private client: ReturnType<typeof createDataApiClient>

  constructor(config: { serviceToken?: string; baseUrl?: string } = {}) {
    // Only pass serviceToken if it's defined
    const clientConfig: any = {}
    if (config.serviceToken) {
      clientConfig.headers = {
        Authorization: `Bearer ${config.serviceToken}`,
        'Content-Type': 'application/json',
      }
    }
    if (config.baseUrl) {
      clientConfig.baseUrl = config.baseUrl
    }

    this.client = createDataApiClient(clientConfig)
  }

  // ============================================================================
  // SEARCH METHODS
  // ============================================================================

  /**
   * Process a search request and return the generated filter set
   */
  async search(request: SearchRequest): Promise<SearchResponse> {
    const response = await this.client.POST('/search', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to process search: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get autocomplete suggestions for a query
   */
  async autocomplete(request: AutocompleteRequest): Promise<AutocompleteResponse> {
    const response = await this.client.POST('/autocomplete', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to get autocomplete suggestions: ${response.error}`)
    }

    return response.data
  }

  // ============================================================================
  // SPECIMEN METHODS
  // ============================================================================

  /**
   * Get specimens by filter criteria
   */
  async getSpecimensByFilter(request: SpecimenByFilterRequest): Promise<SpecimenResponse> {
    const response = await this.client.POST('/specimen/get-by-filter', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to get specimens by filter: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get a single specimen by ID
   */
  async getSpecimenById(request: SpecimenByIdRequest): Promise<SpecimenResponse> {
    const response = await this.client.POST('/specimen/get-by-id', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to get specimen by ID: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get specimen count based on filter criteria
   */
  async getSpecimenCount(request: SpecimenCountRequest): Promise<SpecimenCountResponse> {
    const response = await this.client.POST('/specimen/count', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to get specimen count: ${response.error}`)
    }

    return response.data
  }

  /**
   * Get available filter options for specimens
   */
  async getSpecimenFilterOptions(): Promise<SpecimenFilterOptionsResponse> {
    const response = await this.client.GET('/specimen/filter-options')

    if (!response.data) {
      throw new Error(`Failed to get specimen filter options: ${JSON.stringify(response.error || 'Unknown error')}`)
    }

    return response.data
  }

  // ============================================================================
  // ORGANIZATION METHODS
  // ============================================================================

  /**
   * Get organization statistics
   */
  async getOrganizationStatistics(request: OrganizationRequest): Promise<OrganizationResponse> {
    const response = await this.client.POST('/organization', {
      body: request,
    })

    if (response.error) {
      throw new Error(`Failed to get organization statistics: ${response.error}`)
    }

    return response.data
  }
}

/**
 * Create a Data API server client
 * Similar to Clerk's createClerkClient()
 */
export function createDataApiServerClient(config: { serviceToken?: string; baseUrl?: string } = {}) {
  return new DataApiServerClient(config)
}

// Export types
export type {
  SearchRequest,
  SearchResponse,
  SpecimenByFilterRequest,
  SpecimenByIdRequest,
  SpecimenCountRequest,
  SpecimenCountResponse,
  SpecimenResponse,
  SpecimenFilterOptionsResponse,
  AutocompleteRequest,
  AutocompleteResponse,
  OrganizationRequest,
  OrganizationResponse,
} from './'
